import type { CSSProperties } from 'react';
import { AlertTriangle, OctagonAlert } from 'lucide-react';
import type { OrcamentoExibicao, OrcamentoMc } from '@triade/contracts';
import { OrigemLucro } from '@/components/origem-lucro';
import { SeloFaixaMc } from '@/components/margem-contribuicao';
import { NOTA_INFORMATIVO, REGIME_ROTULO, SeloEstimada } from '@/components/reforma/reforma-ui';
import { ehDecimal, ehZero, formatarContagem, formatarDecimal, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface AvisoOrcamento {
  code: string;
  message: string;
}

/** warnings do motor (unknown[] no contrato) -> {code, message}. */
export function lerAvisos(warnings: unknown[] | undefined): AvisoOrcamento[] {
  return (warnings ?? []).flatMap((w) => {
    const x = w as Partial<AvisoOrcamento> | null;
    return x && typeof x.code === 'string' ? [{ code: x.code, message: String(x.message ?? x.code) }] : [];
  });
}

const COR_FAIXA = { green: 'text-success-ink', orange: 'text-warning-ink', red: 'text-danger-ink' } as const;
const ESCALA = '0.3';

const dec = (v: string | null | undefined): string => (ehDecimal(v) ? v.trim() : '0');

/**
 * Faixa de lucro: vermelho ate o minimo, laranja ate a meta, verde acima (escala fixa 0..30% do design).
 * Sem aritmetica em JS: as posicoes sao `calc()` do CSS sobre as fracoes que a API entregou.
 */
export function FaixaLucro({ lucro, meta, minimo }: { lucro: string | null; meta: string; minimo: string | null }) {
  const estilo = {
    '--min': dec(minimo),
    '--meta': dec(meta),
    '--lucro': dec(lucro),
    '--escala': ESCALA,
    background:
      'linear-gradient(to right, #FECACA 0%, #FECACA calc(var(--min) / var(--escala) * 100%), #FED7AA calc(var(--min) / var(--escala) * 100%), #FED7AA calc(var(--meta) / var(--escala) * 100%), #BBF7D0 calc(var(--meta) / var(--escala) * 100%), #BBF7D0 100%)',
  } as CSSProperties;
  const descricao =
    lucro === null
      ? 'Sem lucro calculado'
      : `Lucro ${formatarFracaoPercentual(lucro)} entre o mínimo de ${formatarFracaoPercentual(minimo)} e a meta de ${formatarFracaoPercentual(meta)}`;
  return (
    <>
      <div className="relative mt-2.5 h-2.5 rounded-full" style={estilo} role="img" aria-label={descricao}>
        {lucro !== null && (
          <div
            data-testid="marcador-lucro"
            className="absolute -top-1 h-[18px] w-1 rounded-sm bg-ink [left:clamp(0%,calc(var(--lucro)/var(--escala)*100%),100%)]"
          />
        )}
      </div>
      <div className="tabular mt-1.5 flex justify-between text-micro text-ink-muted">
        <span>0%</span>
        <span>mín. {formatarFracaoPercentual(minimo)}</span>
        <span>meta {formatarFracaoPercentual(meta)}</span>
        <span>30%</span>
      </div>
    </>
  );
}

export interface ResumoOrcamentoProps {
  exibicao: OrcamentoExibicao | undefined;
  consideraFreteLucro: boolean;
  carregando?: boolean;
  /** MC do orcamento gravado (itens, sem frete). Ausente na simulacao e em versao antiga: nada aparece. */
  mc?: OrcamentoMc;
}

/** Coluna "Resumo": receita total, lucro do orcamento (com faixa) e totais. Tudo da API, so formatado. */
export function ResumoOrcamento({ exibicao, consideraFreteLucro, carregando = false, mc }: ResumoOrcamentoProps) {
  if (!exibicao) {
    return (
      <section aria-label="Totais" className="rounded-2xl border border-border bg-card p-5 text-small text-ink-muted">
        <p role={carregando ? 'status' : undefined}>{carregando ? 'Calculando…' : 'Os totais aparecem quando o orçamento estiver completo (tabela, condição e itens).'}</p>
      </section>
    );
  }
  const e = exibicao;
  return (
    <section aria-label="Totais" aria-busy={carregando} className={cn('rounded-2xl border border-border bg-card p-5', carregando && 'opacity-70')}>
      <div className="text-small text-ink-muted">Receita total</div>
      <div data-testid="receita-total" className="tabular min-w-0 max-w-full break-words text-[30px] font-semibold tracking-[-0.01em]">
        {formatarMoeda(e.receitaTotal)}
      </div>
      <div className="mt-[18px] flex items-baseline justify-between">
        <div className="text-small text-ink-muted">Lucro do orçamento{consideraFreteLucro ? ' (com frete)' : ''}</div>
        <div data-testid="lucro-total" className={cn('tabular text-metric font-semibold', e.faixa ? COR_FAIXA[e.faixa] : 'text-ink-muted')}>
          {e.lucroExibido === null ? '—' : formatarFracaoPercentual(e.lucroExibido)}
        </div>
      </div>
      <FaixaLucro lucro={e.lucroExibido} meta={e.metaLucro} minimo={e.minimoLucro} />
      <OrigemLucro desejado={e.metaLucroOrigem} minimo={e.minimoLucroOrigem} className="mt-1 block" />
      <dl className="mt-[18px] flex flex-col gap-2 border-t border-line-soft pt-3.5 text-small">
        <Linha rotulo="Itens" valor={`${formatarDecimal(e.quantidadeTotal, 0)} un · ${e.itens.length} ${e.itens.length === 1 ? 'produto' : 'produtos'}`} />
        <Linha rotulo="Receita dos itens" valor={formatarMoeda(e.receitaItens)} />
        <Linha rotulo="Frete" valor={formatarMoeda(e.freteReceita)} />
        {e.freteDetalhe?.modalidade === 'CIF' && (
          <Linha
            rotulo={e.freteDetalhe.freteNet !== undefined ? `Frete CIF (net ${formatarMoeda(e.freteDetalhe.freteNet)})` : 'Frete CIF'}
            valor={e.freteDetalhe.destacado ? 'Frete destacado' : 'Frete incluso no preço'}
          />
        )}
        {e.freteDetalhe?.modalidade === 'FOB' && <Linha rotulo="Frete FOB" valor="por conta do cliente" />}
        {consideraFreteLucro && e.lucro != null && <Linha rotulo="Lucro sem frete" valor={formatarFracaoPercentual(e.lucro)} />}
        <Linha rotulo="IPI" valor="incluso nos preços" />
        {e.reforma && (
          <>
            <Linha rotulo="Regime da reforma" valor={`${e.reforma.etapa} · ${REGIME_ROTULO[e.reforma.regime]}`} />
            <Linha rotulo={e.reforma.informativo ? '+ CBS/IBS (informativo)' : '+ CBS/IBS'} valor={formatarMoeda(e.reforma.cbsIbsTotal)} />
            {!ehZero(e.reforma.isTotal) && <Linha rotulo="Imposto Seletivo" valor={formatarMoeda(e.reforma.isTotal)} />}
            <Linha rotulo="Total da NF" valor={formatarMoeda(e.reforma.nfTotal)} />
          </>
        )}
      </dl>
      {e.reforma?.aliquotaEstimada && <SeloEstimada className="mt-3" />}
      {e.reforma?.informativo && <p className="mt-2 text-caption text-ink-muted">{NOTA_INFORMATIVO}</p>}
      {mc && (
        <div data-testid="mc-orcamento" className="mt-[18px] flex flex-col gap-2 border-t border-line-soft pt-3.5 text-small">
          <div className="flex items-start justify-between gap-3">
            <div className="text-ink-muted">Margem de contribuição</div>
            <div className="flex flex-col items-end gap-0.5">
              {mc.pct === null ? <span className="text-ink-disabled">—</span> : <SeloFaixaMc faixa={mc.faixa} rotulo={formatarFracaoPercentual(mc.pct)} />}
              {mc.valor !== undefined && <span className="tabular">{formatarMoeda(mc.valor)}</span>}
            </div>
          </div>
          {mc.itensPrejuizoMarginal > 0 && (
            <p role="status" className="font-medium text-danger-ink">
              {formatarContagem(mc.itensPrejuizoMarginal)} {mc.itensPrejuizoMarginal === 1 ? 'item com prejuízo marginal' : 'itens com prejuízo marginal'}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-ink-secondary">{rotulo}</dt>
      <dd className="tabular">{valor}</dd>
    </div>
  );
}

export interface AlertaLucroProps {
  exibicao: OrcamentoExibicao | undefined;
  avisos: AvisoOrcamento[];
}

/** Warning do motor LUCRO_ABAIXO_DO_MINIMO (vermelho) ou aviso de itens abaixo da meta (laranja). */
export function AlertaLucro({ exibicao, avisos }: AlertaLucroProps) {
  const minimo = avisos.find((a) => a.code === 'LUCRO_ABAIXO_DO_MINIMO');
  const abaixo = exibicao ? exibicao.itens.filter((i) => i.faixa !== 'green') : [];
  if (minimo) {
    return (
      <section role="alert" aria-label="Lucro abaixo do mínimo" className="rounded-2xl border border-destructive/30 bg-danger-bg px-[18px] py-4">
        <div className="flex items-center gap-2 font-semibold text-danger-ink">
          <OctagonAlert aria-hidden="true" className="size-[18px]" />
          Lucro abaixo do mínimo
        </div>
        <div className="mt-1 text-small text-danger-ink">{minimo.message}</div>
        <div className="tabular mt-1 text-caption text-ink-muted">código {minimo.code}</div>
      </section>
    );
  }
  if (abaixo.length > 0) {
    return (
      <section aria-label="Itens abaixo da meta" className="rounded-2xl border border-warning-dot/40 bg-warning-bg px-[18px] py-4">
        <div className="flex items-center gap-2 font-semibold text-warning-ink">
          <AlertTriangle aria-hidden="true" className="size-[18px]" />
          {abaixo.length} {abaixo.length === 1 ? 'item abaixo da meta' : 'itens abaixo da meta'}
        </div>
        <div className="mt-1 text-small text-warning-ink">
          Abaixo da meta de {formatarFracaoPercentual(exibicao?.metaLucro)}: {abaixo.map((i) => `${i.codigo} (${formatarFracaoPercentual(i.lucro)})`).join(', ')}.
        </div>
      </section>
    );
  }
  return null;
}
